/**
 * Site Restrictions - Configuration-based display restrictions
 *
 * These utility functions check site configuration restrictions for:
 * - URL exclusion patterns
 * - Mobile device compatibility
 * - Device type restrictions
 *
 * Used by SiteConfigContext for infrastructure-level checks.
 * Widget initialization logic uses these utilities in combination with traffic control.
 */

import { getDeploymentTarget } from '../../config/deployment';
import type { SiteConfig } from '../../config/types';
import { logger } from '../core/logger';
import { isMobileDevice } from '../device/mobileDetection';
import { matchesUrlPattern } from '../web/urlUtils';
import { resolvePageDisplayRule } from './pageDisplayRules';

/**
 * Check if a URL is allowed based on site configuration patterns.
 *
 * Evaluates two layers:
 * 1. display_only_on_urls — allowlist; if set, URL must match at least one pattern
 * 2. page_display_rules  — first-match rules; a "hidden" match blocks the URL
 *
 * @param siteConfig - Site configuration from database
 * @param url - URL to check against patterns
 * @returns true if URL is allowed, false if URL should be hidden
 */
export function isUrlAllowed(siteConfig: SiteConfig | null | undefined, url: string): boolean {
    const displayOnlyOnUrls = siteConfig?.display_only_on_urls ?? [];
    const pageDisplayRules = siteConfig?.custom_config?.page_display_rules ?? [];

    // Layer 1: allowlist — URL must match at least one pattern
    // Skipped for non-production deployments (preprod, demo-page, backend-ui)
    // because they run on localhost/internal domains that never match client URLs.
    const deploymentTarget = getDeploymentTarget();
    const enforceAllowlist =
        !deploymentTarget ||
        deploymentTarget === 'widget' ||
        deploymentTarget === 'chrome-extension';
    if (enforceAllowlist && displayOnlyOnUrls.length > 0) {
        const isIncluded = displayOnlyOnUrls.some((pattern) => matchesUrlPattern(url, pattern));
        if (!isIncluded) {
            logger.debug(`⚠️ URL "${url}" blocked by display_only_on_urls allowlist`);
            return false;
        }
    }

    // Layer 2: page display rules — a "hidden" match blocks the URL
    if (pageDisplayRules.length > 0) {
        const matchedRule = resolvePageDisplayRule(pageDisplayRules, url);
        if (matchedRule?.display === 'hidden') {
            logger.debug(`⚠️ URL "${url}" blocked by page_display_rules (hidden)`);
            return false;
        }
    }

    return true;
}

/** Strip the scheme and everything from the first path separator, leaving the host pattern. */
function extractPatternHost(pattern: string): string | null {
    const match = pattern.trim().match(/^https?:\/\/([^/?#]+)/i);
    return match ? match[1] : null;
}

/** Drop a leading "www." so "www.example.com" and "example.com" compare equal. */
function stripWww(host: string): string {
    return host.toLowerCase().replace(/^www\./, '');
}

/**
 * Check whether the URL's *host* is covered by the display_only_on_urls allowlist.
 *
 * Deliberately coarser than {@link isUrlAllowed}: it asks "is this host in scope at
 * all", not "is this page allowed". A blocked path on a listed host (an under-specified
 * allowlist entry, or a page_display_rules hide) still counts as in scope — only a host
 * that appears nowhere in the allowlist is out of scope, e.g. a client's logged-in
 * product app picked up through the config resolver's root-domain fallback.
 *
 * @param siteConfig - Site configuration from database
 * @param url - URL to check
 * @returns true if the host is in scope (or the allowlist is not enforced)
 */
export function isHostInAllowlistScope(
    siteConfig: SiteConfig | null | undefined,
    url: string
): boolean {
    const displayOnlyOnUrls = siteConfig?.display_only_on_urls ?? [];

    // Mirror isUrlAllowed: non-production targets run on localhost/internal domains
    // that never match client URLs, so the allowlist is not enforced for them.
    const deploymentTarget = getDeploymentTarget();
    const enforceAllowlist =
        !deploymentTarget ||
        deploymentTarget === 'widget' ||
        deploymentTarget === 'chrome-extension';
    if (!enforceAllowlist || displayOnlyOnUrls.length === 0) {
        return true;
    }

    let hostname: string;
    try {
        hostname = stripWww(new URL(url).hostname);
    } catch {
        // Unparseable URL — fail open, this gate only ever removes telemetry.
        return true;
    }

    return displayOnlyOnUrls.some((pattern) => {
        const patternHost = extractPatternHost(pattern);
        // Scheme-less or relative patterns say nothing about the host; treat as in scope.
        if (!patternHost) {
            return true;
        }
        const normalized = stripWww(patternHost);
        if (!normalized.includes('*')) {
            return normalized === hostname;
        }
        // Wildcard host (e.g. "msr.skello.*") — same * → .* expansion as matchesUrlPattern.
        const escaped = normalized.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*');
        return new RegExp(`^${escaped}$`).test(hostname);
    });
}

/**
 * Check if mobile display is enabled in site configuration
 *
 * @param siteConfig - Site configuration from database
 * @returns true if mobile display is explicitly enabled, false otherwise
 */
export function isMobileEnabled(siteConfig: SiteConfig | null): boolean {
    // If mobile display is explicitly enabled in custom config, allow it
    if (siteConfig?.custom_config?.enable_mobile === true) {
        logger.debug(`✅ Mobile display is explicitly enabled in config`);
        return true;
    }
    logger.debug(`❌ Mobile display is not enabled in config`);
    // Default behavior: don't display on mobile if not explicitly enabled
    return false;
}

/**
 * Check if the current device is allowed based on device type and site configuration
 *
 * @param siteConfig - Site configuration from database
 * @returns true if current device is allowed, false otherwise
 */
export function isCurrentDeviceAllowed(siteConfig: SiteConfig | null): boolean {
    const isMobile = isMobileDevice();

    // If it's a mobile device, check if mobile display is enabled
    if (isMobile) {
        logger.debug(`📱 Mobile device detected - checking config`);
        return isMobileEnabled(siteConfig);
    }

    // Always allow desktop/non-mobile devices
    logger.debug(`💻 Desktop device detected - allowed`);
    return true;
}
